import * as React from 'react';
import { Marker, MarkerProps } from '@react-google-maps/api';
import { PinState, PinType, getBusinessPinIcon } from 'utils/mapsUtils';
import { Theme, makeStyles, useTheme } from '@material-ui/core/styles';
import { useIsSakeIconSetEnabled } from 'omakase/utils/IconMigrationUtil';
import { useIsVisglMapsEnabled } from 'featureFlags/useIsVisglMapsEnabled';
import ConsumerMarkerNew from 'components/maps/ConsumerMarkerNew';
import _ from 'lodash';

interface Props extends Exclude<Omit<MarkerProps, 'onClick' | 'position' | 'label'>, 'icon'> {
  onClick(id: number): void;
  isSelected: boolean;
  lat: number;
  lng: number;
  id: number;
  label?: string;
  pinType?: PinType;
}

const useStyles = makeStyles((theme: Theme) => ({
  labelStyle: {
    textShadow: `-1px -1px 0 ${theme.colors.bgDefault},  1px -1px 0 ${theme.colors.bgDefault}, -1px 1px 0 ${theme.colors.bgDefault}, 1px 1px 0 ${theme.colors.bgDefault}`,
  },
}));

/**
 * Styled map marker that renders a selected and default state
 */
const ConsumerMarker: React.FunctionComponent<Props> = React.memo(
  ({ isSelected, onClick, id, lat, lng, label, pinType, ...rest }) => {
    const visglEnabled = useIsVisglMapsEnabled();
    const [isMouseOver, setIsMouseOver] = React.useState(false);
    const [hasBeenVisited, setHasBeenVisited] = React.useState(false);
    const sakeIconsEnabled = useIsSakeIconSetEnabled();
    const styles = useStyles();
    const theme = useTheme();
    const newPinSize = isSelected ? 38 : 32;

    const handleClick = React.useCallback(() => {
      setHasBeenVisited(true);
      onClick(id);
    }, [id, onClick]);

    const handleMouseOver = () => {
      setIsMouseOver(true);
    };

    const handleMouseOut = () => {
      setIsMouseOver(false);
    };

    const getPinState = (): PinState => {
      if (isSelected) {
        return 'selected';
      }

      if (hasBeenVisited) {
        return 'visited';
      }

      return 'enabled';
    };

    const pinState = getPinState();

    const text = React.useMemo(() => _.truncate(label || '', { length: 44 }), [label]);

    const markerLabel: google.maps.MarkerLabel | undefined =
      isMouseOver && text
        ? {
            text,
            className: styles.labelStyle,
            fontSize: '14px',
            color: theme.colors.fgDefault,
            fontWeight: theme.typography.fontWeightMedium?.toString(),
          }
        : undefined;

    // TODO(visgl-burn-in): remove this flag fork and the legacy `<Marker>` branch
    // below (keep ConsumerMarkerNew) once CONSUMER_ENABLE_GOOGLE_MAPS_API_UPDATE is
    // fully rolled out.
    if (visglEnabled) {
      return (
        <ConsumerMarkerNew
          isSelected={isSelected}
          onClick={onClick}
          id={id}
          lat={lat}
          lng={lng}
          label={label}
          pinType={pinType}
        />
      );
    }

    return (
      <Marker
        {...rest}
        position={{ lat, lng }}
        icon={getBusinessPinIcon(newPinSize, pinState, pinType, { sakeIconsEnabled })}
        onClick={handleClick}
        label={markerLabel}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        zIndex={isMouseOver ? 9999999 : pinState === 'selected' ? 9999998 : undefined}
      />
    );
  }
);

export default ConsumerMarker;
